import { Paragraph, WrapLgMarginBottom } from "App/global-style";
import {
  findNotSuperReason,
  getInitialFormStateWithCorrectCoverage,
} from "Features/CheckPrice/Products/RiskProducts/HouseProducts/CabinContents/ProductPrice/cabinContentsProductPriceUtils";
import {
  useCompletedFormState,
  useCoveragePriceCalculations,
  useFormState,
} from "Store/priceCalculation/priceCalculationHooks";
import { Form, FormikProps } from "formik";
import { CABIN_CONTENTS_ABOUT_STEP, CABIN_CONTENTS_PRICE_STEP } from "..";
import {
  CABIN_CODE,
  CABIN_VALUE,
  filterCabinDeductibleNotAvailableForCustomer,
  getCoveragesFilteredOnStandardSuper,
  getNoSuperAlertDescriptions,
} from "../../houseProductsUtils";
import {
  CabinContentsProductDetailsFormValues,
  CabinContentsProductPriceFormValues,
  CabinContentsTexts,
} from "../cabinContentsInterfaces";

import { CurrentPriceMessage } from "Components/CurrentPriceMessage/CurrentPriceMessage";
import { ProductBackendName } from "Components/Features/CheckPrice/Products/productTypes";
import { ProductPriceTile } from "Components/Features/CheckPrice/Tiles/ProductPriceTile";
import { BackendErrorAlert } from "Components/Form/CommonFormElements/BackendErrorAlert/BackendErrorAlert";
import { ErrorsSummedUpRender } from "Components/Form/ErrorsSummedUp";
import { NavigationButtons } from "Components/NavigationButtons";
import { getCollectionOfDittoComponents } from "Components/TextContext";
import { TextComponentNameLevel1 } from "Components/TextContext/textConstants";
import { ThemedSpinner } from "Components/Themes/components/ThemedSpinner";
import { useAddProductToCartHandler } from "Store/addProductToCart/hooks";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { CabinContentsCoverage } from "Store/interfaces/products/coverage";
import { ReduxState } from "Store/rootReducer";
import { getFMSProductName } from "Store/texts/helpers/FMSproductName";
import { useProductTexts } from "Store/texts/hooks";
import { mapValidationErrors } from "Utils/formUtils";
import { isDefined } from "Utils/functions";
import { getDeductibleOptions } from "Utils/priceUtils";
import { useUnleashFeatureFlag } from "Utils/unleash";
import { FeatureFlagCabinRental } from "Utils/unleash/flags";
import React from "react";
import { useSelector } from "react-redux";
import { ChooseDeductible } from "../../FormComponentsHouseProducts/ChooseDeductible";
import { CoverageElements } from "../../House/ProductPrice/FormComponents/CoverageElements";
import { DEFAULT_DEDUCTIBLE_CABIN_CONTENTS } from "../cabinContentsConstants";
import { validationSchema } from "./cabinContentsProductPriceValidation";

export const CabinContentsProductPrice = (): React.ReactElement => {
  const [
    {
      formText: { checkPrice },
      details,
    },
    lang,
  ] = useProductTexts<CabinContentsTexts>(
    getFMSProductName(INSURANCE_NAME_VALUE.CABIN_CONTENTS),
  );

  const { cabincontents: coverageCardTextComponents } =
    getCollectionOfDittoComponents(TextComponentNameLevel1.COVERAGE_CARD);

  const handleAddProductToCart = useAddProductToCartHandler(
    CABIN_CONTENTS_PRICE_STEP.id,
  );
  const {
    priceCalculation: { priceCalculationResult },
    addProductToCart,
  } = useSelector((state: ReduxState) => state);

  const enableRentalToggle = useUnleashFeatureFlag(
    FeatureFlagCabinRental.ENABLE_CABIN_RENTAL,
  );

  const cabinContentsProductDetailsFormValues =
    useCompletedFormState<CabinContentsProductDetailsFormValues>(
      CABIN_CONTENTS_ABOUT_STEP.id,
    );

  const notSuperReason = enableRentalToggle
    ? findNotSuperReason(cabinContentsProductDetailsFormValues)
    : null;

  const initialFormState = getInitialFormStateWithCorrectCoverage(
    useFormState<CabinContentsProductPriceFormValues>(
      CABIN_CONTENTS_PRICE_STEP.id,
      {
        coverage: CabinContentsCoverage.CABIN_CONTENTS_EXTENDED,
        deductible: DEFAULT_DEDUCTIBLE_CABIN_CONTENTS,
      },
    ),
    notSuperReason !== null,
  );

  const handleSubmit = (
    formValues: CabinContentsProductPriceFormValues,
  ): void => {
    const productToAdd = {
      coverage: formValues.coverage,
      productType: INSURANCE_NAME_VALUE.CABIN_CONTENTS,
      filterValue: formValues.deductible,
    };
    handleAddProductToCart(formValues, [productToAdd]);
  };

  const coverages = useCoveragePriceCalculations();
  const cabinContentsCoverages = filterCabinDeductibleNotAvailableForCustomer(
    CABIN_CODE,
    CABIN_VALUE,
    coverages,
  );
  return (
    <ProductPriceTile
      header={CABIN_CONTENTS_PRICE_STEP.title}
      initialValues={initialFormState}
      validationSchema={validationSchema()}
      onSubmit={handleSubmit}
    >
      {(formProps: FormikProps<CabinContentsProductPriceFormValues>) => {
        return (
          <Form>
            <WrapLgMarginBottom>
              <Paragraph>{checkPrice.ingress[lang]}</Paragraph>
            </WrapLgMarginBottom>
            {cabinContentsCoverages.length > 0 ? (
              <>
                <ChooseDeductible
                  fieldName="deductible"
                  formProps={formProps}
                  options={getDeductibleOptions(
                    INSURANCE_NAME_VALUE.CABIN_CONTENTS,
                    cabinContentsCoverages,
                  )}
                  label={checkPrice.deductible.label[lang]}
                  hint={checkPrice.deductible.hint[lang]}
                />
                <CoverageElements
                  coverages={getCoveragesFilteredOnStandardSuper(
                    !isDefined(notSuperReason),
                    CabinContentsCoverage.CABIN_CONTENTS_STANDARD,
                  )}
                  selectedCoverage={formProps.values.coverage}
                  coverageStandards={Object.values(CabinContentsCoverage)}
                  coverageFieldName="coverage"
                  deductible={formProps.values.deductible}
                  priceIsLoading={priceCalculationResult.isLoading}
                  title={checkPrice.coverage.title[lang]}
                  product={ProductBackendName.CABIN_CONTENTS}
                  textComponents={coverageCardTextComponents}
                  unableToBuySuper={isDefined(notSuperReason)}
                  alertLabel={getNoSuperAlertDescriptions(notSuperReason)}
                />
                <CurrentPriceMessage
                  productBackendName={ProductBackendName.CABIN_CONTENTS}
                  coverages={coverages}
                  selectedPrices={[
                    {
                      selectedCoverage: formProps.values.coverage,
                      selectedDeductible: formProps.values.deductible,
                      defaultDeductible: undefined,
                    },
                  ]}
                />
              </>
            ) : (
              <ThemedSpinner />
            )}
            <BackendErrorAlert
              priceCalculation={priceCalculationResult}
              addProductToCart={addProductToCart}
            />
            <ErrorsSummedUpRender
              formProps={formProps}
              errors={mapValidationErrors(formProps.errors, details, lang)}
              isVisible
            />
            <NavigationButtons
              isUpsell
              isLoading={addProductToCart.isLoading}
            />
          </Form>
        );
      }}
    </ProductPriceTile>
  );
};
